import React, { useEffect, useState } from "react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell } from "recharts";
import { TrendingUp, PieChart as PieIcon } from "lucide-react";
import { api } from "@/lib/api";
import { money, PLAN_LABEL } from "@/lib/format";

const PLAN_COLORS = { free: "#94a3b8", premium: "#6366f1", super_premium: "#f59e0b" };

function PlanTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return <div className="rounded-lg border border-border bg-card px-3 py-2 text-xs shadow-lg">{d.name}: <span className="font-semibold">{d.value}</span> ({d.pct}%)</div>;
}

export function PlanBreakdown({ plans }) {
  const total = Object.values(plans || {}).reduce((a, b) => a + b, 0);
  const slices = Object.entries(PLAN_LABEL).map(([k, name]) => ({
    key: k, name, value: plans?.[k] || 0, pct: total ? Math.round(((plans?.[k] || 0) / total) * 100) : 0,
  }));
  return (
    <div className="border border-border rounded-lg bg-background p-4 flex flex-col" data-testid="plan-breakdown">
      <div className="flex items-center gap-2 mb-2">
        <PieIcon className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
        <div className="font-display font-bold">Customers by plan</div>
      </div>
      <div className="relative h-48">
        {!plans ? <div className="h-full animate-pulse bg-muted rounded" /> : total === 0 ? (
          <div className="h-full flex items-center justify-center text-sm text-muted-foreground" data-testid="plan-breakdown-empty">No customers yet</div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={slices.filter((s) => s.value > 0)} dataKey="value" nameKey="name" innerRadius="58%" outerRadius="88%" paddingAngle={slices.filter((s) => s.value > 0).length > 1 ? 2 : 0} stroke="none">
                  {slices.filter((s) => s.value > 0).map((s) => <Cell key={s.key} fill={PLAN_COLORS[s.key]} />)}
                </Pie>
                <Tooltip content={<PlanTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="font-display text-2xl font-bold tabular-nums" data-testid="plan-breakdown-total">{total}</div>
              <div className="text-[11px] text-muted-foreground">customers</div>
            </div>
          </>
        )}
      </div>
      <div className="mt-3 space-y-1.5">
        {slices.map((s) => (
          <div key={s.key} className="flex items-center justify-between text-sm" data-testid={`plan-count-${s.key}`}>
            <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: PLAN_COLORS[s.key] }} />{s.name}</span>
            <span className="tabular-nums text-muted-foreground"><span className="text-foreground font-semibold">{s.value}</span> · {s.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const RANGES = [6, 12, 24];

const monthLabel = (k) => {
  const [y, m] = k.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "short", year: "2-digit" });
};

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 text-xs shadow-lg">
      <div className="font-semibold mb-1">{monthLabel(label)}</div>
      <div className="text-indigo-600 dark:text-indigo-400">Revenue: {money(d.revenue)}</div>
      <div className="text-emerald-600 dark:text-emerald-400">Sign-ups: {d.signups}</div>
      <div className="text-muted-foreground">New subscriptions: {d.subscriptions}</div>
    </div>
  );
}

export function RevenueChart() {
  const [range, setRange] = useState(12);
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/owner/monthly", { params: { months: range } }).then((r) => setData(r.data)).catch(() => {});
  }, [range]);
  return (
    <div className="mt-6 grid lg:grid-cols-3 gap-4">
    <div className="lg:col-span-2 border border-border rounded-lg bg-background p-4" data-testid="revenue-chart">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
          <div className="font-display font-bold">Monthly sign-ups & revenue</div>
        </div>
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <button key={r} onClick={() => setRange(r)} data-testid={`revenue-range-${r}`}
              className={`text-xs px-2.5 py-1 rounded transition-colors ${range === r ? "bg-indigo-600 text-white" : "bg-muted text-muted-foreground hover:text-foreground"}`}>{r}m</button>
          ))}
        </div>
      </div>
      {data && (
        <div className="grid grid-cols-3 gap-3 mb-4 text-center">
          <div><div className="text-xs text-muted-foreground">Revenue</div><div className="font-display text-lg font-bold tabular-nums" data-testid="revenue-total">{money(data.totals.revenue)}</div></div>
          <div><div className="text-xs text-muted-foreground">Sign-ups</div><div className="font-display text-lg font-bold tabular-nums" data-testid="signups-total">{data.totals.signups}</div></div>
          <div><div className="text-xs text-muted-foreground">Subscriptions</div><div className="font-display text-lg font-bold tabular-nums" data-testid="subs-total">{data.totals.subscriptions}</div></div>
        </div>
      )}
      <div className="h-64">
        {!data ? <div className="h-full animate-pulse bg-muted rounded" /> : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data.months} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="rev" tickFormatter={(v) => `$${v}`} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="users" orientation="right" allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="rev" dataKey="revenue" name="Revenue ($)" fill="#6366f1" radius={[4, 4, 0, 0]} maxBarSize={36} />
              <Line yAxisId="users" type="monotone" dataKey="signups" name="Sign-ups" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
    <PlanBreakdown plans={data?.plans} />
    </div>
  );
}
